/**
 * Design System — Colors
 *
 * The single source of truth for colour in TypeScript. `theme.css` mirrors these
 * values as custom properties for stylesheets; nothing else should hold a hex.
 *
 * Note: keep theme.css in sync with these values.
 */

export const colors = {
  /* ========================================================================
     Sand Scale — every surface in the app

     The system is warm. These are not greys with a hint of yellow; they are the
     paper the deck is printed on, and a neutral grey dropped among them reads
     as a dead pixel.
     ======================================================================== */
  sand: {
    50: "#fbf9f7",
    100: "#f4f1eb",
    200: "#f0ede7",
    300: "#e9e6e0",
    /** Light cream — warmer than 300, lighter than the 400 hero wash. */
    350: "#eee9dc",
    /** The customer hero wash — the one properly saturated sand. */
    400: "#e8e0cd",
    /** Sticky-note tape. Opaque, because tape is not translucent. */
    500: "#c5c2bd",
  },

  /* ========================================================================
     Ink Scale — text, hairlines, and the primary action
     ======================================================================== */
  ink: {
    /** Headings and every primary button. Not #000: a true black on warm paper
     *  reads as a hole punched in the page. */
    950: "#17140f",
    900: "#262320",
    700: "#55514a",
    600: "#6f6b63",
    500: "#8b877f",
    400: "#a4a099",
    300: "#c2beb7",
    200: "#dcd8d1",
    100: "#e7e3dc",
    50: "#f0ece5",
  },

  white: "#ffffff",

  /* ========================================================================
     Sticky notes

     Six colours, cycled by board position so a board never shows two of the
     same next to each other. The order is the design's — `utils/noteTints.ts`
     walks the matching custom properties in exactly this sequence.
     ======================================================================== */
  note: {
    cream: "#f4f0e7",
    green: "#e6eec0",
    blue: "#dce9fd",
    lavender: "#e3defc",
    peach: "#f2d3b8",
    pink: "#f2a8ac",
    /** A warm near-black. The handwriting face needs the contrast. */
    ink: "#35312b",
  },

  /* ========================================================================
     Amber (warnings, caps exceeded) and Red (refusals)
     ======================================================================== */
  amber: { 700: "#8a6a0b", 500: "#e5b001", 400: "#d8b14a" },

  /** A refused action, and the required-field asterisk. Nothing else is red. */
  red: { 600: "#b81616", 500: "#d91d1d" },

  /** The one surviving blue: the focus ring. A warm ring on warm paper
   *  disappears, which is the whole reason this value is still here. */
  focusRing: "#186bf3",

  /**
   * The Priorities board's quadrant inks.
   *
   * Each is its family's bloom darkened until it holds as TYPE on a pale wash of
   * itself. Not the bloom: `family[5].bloom` set as a heading on a 10% wash of
   * the same green is a heading nobody can find from the back of the room, which
   * is exactly what these four names have to survive.
   */
  quad: { must: "#5f7434", should: "#8a6a0b", could: "#3567a8" },

  /**
   * The round rail in the session header. Only the round in progress is filled
   * and outlined; the rest are transparent, so the eye finds the room's position
   * without reading anything.
   */
  step: {
    fill: "rgb(23 20 15 / 7%)",
    line: "rgb(23 20 15 / 18%)",
    icon: "#17140f",
    iconIdle: "#a4a099",
  },

  /* ========================================================================
     Card Families 1–7

     One entry per family, straight from the deck's `PAL`. `tint` is the card
     face, `bloom` the saturated edge and accents, `ink` the text printed on that
     tint — never a grey, or the card stops looking printed. `cover` is the
     cover-art wash, deliberately a duller value than `bloom`.
     ======================================================================== */
  family: {
    1: { tint: "#e0ecb3", bloom: "#93ae5f", ink: "#232b12", cover: "#9bb16c" },
    2: { tint: "#e2d8ff", bloom: "#9e97ff", ink: "#1c1147", cover: "#a59de9" },
    3: { tint: "#f6e197", bloom: "#e5b001", ink: "#3a2e00", cover: "#ddbc4c" },
    4: { tint: "#fec298", bloom: "#ff723e", ink: "#3a1608", cover: "#ee7949" },
    5: { tint: "#e0ecb3", bloom: "#97b261", ink: "#232b12", cover: "#9bb16c" },
    6: { tint: "#d5e6ff", bloom: "#73acfe", ink: "#06162d", cover: "#85afee" },
    7: { tint: "#ff9aa2", bloom: "#e35254", ink: "#330c0d", cover: "#d55a5d" },
  },

  /* ========================================================================
     Deck-browser card face (NOT the in-session card).

     The `DeckCard` component redraws the printed card art for the deck browser,
     and uses these richer base / glow / ink values sampled from the Figma
     handoff — deeper than the `family.tint/bloom/ink` the in-session `CardFace`
     renders, which is a lighter in-app variant. The two are deliberately
     separate so changing the browser look never touches the card the room picks
     with; a later unification would fold them together.

     Keyed by the STABLE family id `n`, so the handoff's printed numbers are
     remapped here: Reflects 5→6, Partners 6→7. Family 5 (Monitors) is retired
     and never shown in the browser; its entry mirrors family 1 only so the map
     stays total.
     ======================================================================== */
  deck: {
    1: { base: "#d0daa6", glow: "#8ea75c", ink: "#486c00" },
    2: { base: "#ccc4e8", glow: "#a69ce9", ink: "#5147da" },
    3: { base: "#f1de9e", glow: "#e2bd45", ink: "#906e00" },
    4: { base: "#f1bd99", glow: "#ea8a5c", ink: "#ae441d" },
    5: { base: "#d0daa6", glow: "#8ea75c", ink: "#486c00" },
    6: { base: "#b9c6dd", glow: "#6f9cdc", ink: "#1556ba" },
    7: { base: "#e09397", glow: "#c45657", ink: "#7b0204" },
  },

  /**
   * The card families rail's dots and labels (DeckBrowser). A fixed amber
   * accent, not the family's own hue — the curve they sit on reads as one
   * path, not seven.
   */
  rail: {
    dotBorder: "#8a6a00",
    dotBg: "#f9efcc",
    textMuted: "#6c6c6c",
    textActive: "#1b1b1b",
  },

  /**
   * Vote stickers (Pains round). `Exercise.jpg`'s tray cycles six flat hues,
   * not the family ramp. Five already have a token — see `VoteToken.tsx` for
   * the sequence — this pink is the one with no home yet.
   */
  votePink: "#ff58a0",

  /* ========================================================================
     Assembly motion — the screens that draw themselves

     The loading report and the round curtains build their ASCII out of scattered
     glyphs. These are the hues that assembly flies in on, straight from the
     `Report generation & Break screens explorations` doc, which is the source of
     truth for that motion. They are not on any ramp and nothing static uses
     them: a glyph is tinted toward its hue while it is still in flight and lands
     as ink, so the colour only ever exists mid-animation.
     ======================================================================== */
  motion: {
    /** The loader itself. The one place blue is allowed to be a fill. */
    accent: "#1877f2",
    /** The report mark's three plates, top to bottom. */
    mark: { warm: "#c9722f", blue: "#1877f2", leaf: "#7e9a50" },
    /**
     * Where a curtain's glyphs fly in from, by quadrant of the drawing. Four
     * corners, a middle for the blank card's asterisk, and a grey for the frame
     * characters that are only ever scaffolding.
     */
    scan: {
      topLeft: "#1f7a4d",
      topRight: "#2e6fc4",
      bottomLeft: "#c2427d",
      bottomRight: "#a8760e",
      middle: "#c74430",
      frame: "rgb(124 135 148 / 60%)",
    },
    /** What every glyph settles to, in flight and at rest. */
    settled: "rgb(23 20 15 / 88%)",
  },

  /* ========================================================================
     Alpha layer (washes, scrims, hairlines)

     Kept separate because these are not steps on any ramp — they are the ink at
     different strengths, laid over whatever is behind them. Written as
     `rgb(... / ...)` so they compose in both TS and CSS.
     ======================================================================== */
  alpha: {
    lineSoft: "rgb(23 20 15 / 6%)",
    line: "rgb(23 20 15 / 10%)",
    lineStrong: "rgb(23 20 15 / 18%)",
    fill: "rgb(23 20 15 / 4%)",
    fillHover: "rgb(23 20 15 / 6%)",
    inkWash: "rgb(23 20 15 / 7%)",
    inkWashStrong: "rgb(23 20 15 / 11%)",
    amberWash: "rgb(229 176 1 / 18%)",
    dangerWash: "rgb(217 29 29 / 8%)",
    /** Full-screen scrim behind a curtain. */
    scrim: "rgb(23 20 15 / 55%)",
    /** Behind a dialog — lighter, so the screen behind stays legible. */
    dialogScrim: "rgb(23 20 15 / 26%)",
    /** Heavier, for a dialog holding nothing unsaved. */
    dialogScrimStrong: "rgb(23 20 15 / 34%)",
    noteLabel: "rgb(53 49 43 / 52%)",
    noteLine: "rgb(53 49 43 / 22%)",
    noteLineSoft: "rgb(53 49 43 / 12%)",
  },
} as const

export type FamilyNumber = keyof typeof colors.family

/* ==========================================================================
   Semantic
   ========================================================================== */

/**
 * What a colour is FOR, rather than what it is.
 *
 * There is one surface, so there is one set of roles. In CSS these are the
 * `--color-*` properties in `theme.css` and stylesheets should use those; this
 * export is for the handful of cases that must be computed in TS.
 */
export const semantic = {
  surface: {
    textPrimary: colors.ink[950],
    textSecondary: colors.ink[700],
    textMuted: colors.ink[600],
    textFaint: colors.ink[400],
    page: colors.sand[100],
    raised: colors.sand[50],
    raisedHover: colors.white,
    /** True white — a level above a card, not a substitute for one. */
    panel: colors.white,
    sunken: colors.sand[200],
    selected: colors.sand[300],
    dialog: colors.white,
    borderSubtle: colors.alpha.lineSoft,
    borderDefault: colors.alpha.line,
    borderStrong: colors.alpha.lineStrong,
  },
  /** The primary action is ink. Hover and pressed are lifts of the same colour:
   *  a black button that changes hue on hover reads as a different button. */
  action: {
    primary: colors.ink[950],
    primaryHover: colors.ink[900],
    primaryPressed: "#000000",
    primaryWash: colors.alpha.inkWash,
    onPrimary: colors.white,
    /** Locked in: an action already taken, so it reads as spent, not pressable. */
    locked: "#cecac3",
    onLocked: colors.sand[50],
  },
  status: {
    /** A cap exceeded, a room out of time — never a primary button. */
    warning: colors.amber[500],
    warningWash: colors.alpha.amberWash,
    warningText: colors.amber[700],
    /** Finished: everyone done, a frame that holds, a placed card. */
    done: "#97b261",
    error: colors.red[600],
    errorWash: colors.alpha.dangerWash,
  },
  /**
   * A session's state, as a dot. Deliberately borrowed from the family palette
   * so the app has one set of hues: live is family 5, complete is family 2,
   * paused is family 7. Draft is the only value with no family behind it.
   */
  sessionStatus: {
    live: "#97b261",
    draft: "#f4936f",
    complete: "#9e97ff",
    paused: "#e35254",
  },
} as const
